/**
 * ============================================================================
 * [P0-02] Format Utilities - 순수 함수
 *
 * 포맷팅 관련 유틸리티 함수들
 * - DOM 의존성 없음 (순수 함수)
 * - TypeScript 타입 적용
 *
 * 기존 js/utils.js에서 추출 및 순수함수화
 * ============================================================================
 */

// ============================================================================
// [순수 함수] 내용에서 제목 추출
// - 첫 줄 또는 첫 50자를 제목으로 추출
// - DOM 의존성 없음
// ============================================================================
export function extractTitleFromContent(content: string | null | undefined): string {
  if (!content) return '제목 없음'

  const firstLine = content.split('\n')[0].trim()

  if (firstLine.length > 0) {
    return firstLine.length > 50 ? `${firstLine.substring(0, 50)}...` : firstLine
  }

  return content.length > 50 ? `${content.substring(0, 50)}...` : content
}

// ============================================================================
// [순수 함수] HTML 이스케이프 (DOM 의존성 제거 버전)
// - 특수 문자를 HTML 엔티티로 변환
// - 줄바꿈을 <br> 태그로 변환
// - 기존: document.createElement 사용 -> 순수 문자열 처리로 변경
// ============================================================================
export function escapeHtml(text: string | null | undefined): string {
  if (!text) return ''

  // HTML 특수 문자 이스케이프 (순수 문자열 처리)
  const escaped = text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')

  // 줄바꿈을 <br> 태그로 변환
  return escaped.replace(/\n/g, '<br>')
}

// ============================================================================
// [순수 함수] HTML 이스케이프 (줄바꿈 유지, br 태그 없음)
// - React에서 줄바꿈은 CSS white-space로 처리할 때 사용
// ============================================================================
export function escapeHtmlPreserveNewlines(text: string | null | undefined): string {
  if (!text) return ''

  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;')
}

// ============================================================================
// [순수 함수] 날짜 포맷팅 (YYYY. MM. DD. 오전/오후 HH:MM)
// - Date 객체 또는 Firestore Timestamp 지원
// - DOM 의존성 없음
// ============================================================================
type DateInput = Date | { toDate: () => Date } | string | number | null | undefined

export function formatDate(dateObj: DateInput): string {
  if (!dateObj) return '날짜 없음'

  // Firestore Timestamp 또는 일반 Date 처리
  let date: Date

  if (typeof dateObj === 'object' && 'toDate' in dateObj && typeof dateObj.toDate === 'function') {
    // Firestore Timestamp
    date = dateObj.toDate()
  } else if (dateObj instanceof Date) {
    date = dateObj
  } else {
    // string 또는 number (timestamp)
    date = new Date(dateObj as string | number)
  }

  // 유효하지 않은 날짜 체크
  if (isNaN(date.getTime())) return '날짜 없음'

  return date.toLocaleString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  })
}

// ============================================================================
// [순수 함수] 상대적 시간 표시 (예: "3분 전", "2시간 전")
// - 현재 시간 기준 상대적 시간 계산
// ============================================================================
export function formatRelativeTime(dateObj: DateInput): string {
  if (!dateObj) return '날짜 없음'

  let date: Date

  if (typeof dateObj === 'object' && 'toDate' in dateObj && typeof dateObj.toDate === 'function') {
    date = dateObj.toDate()
  } else if (dateObj instanceof Date) {
    date = dateObj
  } else {
    date = new Date(dateObj as string | number)
  }

  if (isNaN(date.getTime())) return '날짜 없음'

  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return '방금 전'
  if (diffMin < 60) return `${diffMin}분 전`
  if (diffHour < 24) return `${diffHour}시간 전`
  if (diffDay < 7) return `${diffDay}일 전`

  // 7일 이상이면 전체 날짜 표시
  return formatDate(date)
}

// ============================================================================
// [순수 함수] 글자 수 포맷팅 (천 단위 콤마)
// ============================================================================
export function formatNumber(num: number): string {
  return num.toLocaleString('ko-KR')
}

// ============================================================================
// [순수 함수] 바이트 단위 포맷팅 (KB, MB, GB)
// ============================================================================
export function formatBytes(bytes: number, decimals: number = 2): string {
  if (bytes === 0) return '0 Bytes'

  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(decimals))} ${sizes[i]}`
}

// ============================================================================
// [순수 함수] 텍스트 자르기 (말줄임표 추가)
// ============================================================================
export function truncateText(text: string, maxLength: number): string {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return `${text.substring(0, maxLength)}...`
}
